/** Checks whether a string is a valid CSS colour (hex, rgb, or hsl). */
export const isColourString = (colour: string): boolean => {
    return (
        /^#([0-9A-F]{3}){1,2}$/i.test(colour) ||
        /^rgb\((\d{1,3}%?,\s*){2}\d{1,3}%?\)$/i.test(colour) ||
        /^hsl\(\d{1,3},\s*\d{1,3}%,\s*\d{1,3}%\)$/i.test(colour)
    );
};

/**
 * WCAG 2.x colour contrast ratio utility.
 *
 * Computes the contrast ratio between two colours using the
 * standard relative-luminance formula defined in WCAG 2.x.
 *
 * Accepts hex strings ('#rrggbb', '#rgb') and CSS rgb/rgba strings
 * ('rgb(r,g,b)', 'rgba(r,g,b,a)').
 *
 * @see https://www.w3.org/TR/WCAG20/#contrast-ratiodef
 */

/** Regex for rgb(r,g,b) and rgba(r,g,b,a) with optional spaces. */
const RGB_RE = /^rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/;

/**
 * Parses a CSS colour string into [r, g, b] (0–255).
 * Supports '#rrggbb', '#rgb', 'rgb(r,g,b)', and 'rgba(r,g,b,a)'.
 */
export function parseColour(colour: string): [number, number, number] {
    const rgbMatch = colour.match(RGB_RE);
    if (rgbMatch) {
        return [Number(rgbMatch[1]), Number(rgbMatch[2]), Number(rgbMatch[3])];
    }

    let c = colour.replace('#', '');
    if (c.length === 3)
        c =
            c.charAt(0) +
            c.charAt(0) +
            c.charAt(1) +
            c.charAt(1) +
            c.charAt(2) +
            c.charAt(2);
    const n = parseInt(c, 16) || 0;
    return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

/** Linearises a single sRGB channel (0–255) per the WCAG 2.x spec. */
function linearise(value: number): number {
    const s = value / 255;
    return s <= 0.04045 ? s / 12.92 : ((s + 0.055) / 1.055) ** 2.4;
}

/** Computes WCAG 2.x relative luminance from [r, g, b]. */
function luminance(rgb: [number, number, number]): number {
    return (
        0.2126 * linearise(rgb[0]) +
        0.7152 * linearise(rgb[1]) +
        0.0722 * linearise(rgb[2])
    );
}

/**
 * Computes the WCAG 2.x contrast ratio between two CSS colours.
 *
 * @param colour   - CSS colour to evaluate (e.g. '#fcfdfd' or 'rgb(252,253,253)').
 * @param baseline - CSS colour to compare against (e.g. '#ffffff' or 'rgb(255,255,255)').
 * @returns Contrast ratio from 1.0 (identical) to 21.0 (black vs white),
 *          rounded to three decimal places.
 */
export function contrastRatio(colour: string, baseline: string): number {
    const l1 = luminance(parseColour(colour));
    const l2 = luminance(parseColour(baseline));
    const lighter = Math.max(l1, l2);
    const darker = Math.min(l1, l2);
    const ratio = (lighter + 0.05) / (darker + 0.05);
    return Math.round(ratio * 1000) / 1000;
}

const PAGE_BACKGROUND = '#ffffff';

/** Fully transparent logo backgrounds stored by the CMS. */
const TRANSPARENT_COLOUR_VALUES = new Set(['transparent', 'rgba(0,0,0,0)']);

/** Common CSS named colours that parseColour cannot hex-decode. */
const CSS_NAMED_COLOURS: Readonly<Record<string, string>> = {
    white: '#ffffff',
    snow: '#fffafa',
    ivory: '#fffff0',
    floralwhite: '#fffaf0',
    ghostwhite: '#f8f8ff',
    whitesmoke: '#f5f5f5',
    seashell: '#fff5ee',
    oldlace: '#fdf5e6',
    linen: '#faf0e6',
    antiquewhite: '#faebd7',
};

/**
 * Contrast vs white below which a circular logo badge needs a visible edge.
 * ~rgb(230, 230, 230) on white — catches off-whites and the CMS default `white`.
 */
const LIGHT_LOGO_BORDER_CONTRAST_THRESHOLD = 1.25;

/**
 * Normalises stored logo background colours before contrast checks.
 * Returns null when the value means "no visible background" (empty/transparent).
 */
function normalizeLogoBackgroundColour(colour: string): string | null {
    const trimmed = colour.trim();
    if (!trimmed) return null;

    const compact = trimmed.replace(/\s+/g, '').toLowerCase();
    if (TRANSPARENT_COLOUR_VALUES.has(compact)) return null;

    const named = CSS_NAMED_COLOURS[trimmed.toLowerCase()];
    if (named) return named;

    return trimmed;
}

/**
 * Returns true when a logo background colour is too close to the page
 * background to produce a visible edge, meaning a default grey border
 * should be rendered instead.
 *
 * Used for professional logos in search-result tiles, tagging modals,
 * HDE print tickets, and BM project-network linking.
 */
export function needsDefaultBorder(
    logoBackground: string | null | undefined,
): boolean {
    if (!logoBackground?.trim()) return true;

    const normalized = normalizeLogoBackgroundColour(logoBackground);
    if (normalized === null) return true;

    return (
        contrastRatio(normalized, PAGE_BACKGROUND) <
        LIGHT_LOGO_BORDER_CONTRAST_THRESHOLD
    );
}
